/**
 * Counter building blocks. Every figure shown here is a number the backend sent, written with the F0 money
 * formatter; nothing is added, subtracted or derived. Comparing a server figure with zero decides only which
 * words and colours to show, never an amount.
 */
import { useRef } from 'react';
import { Badge } from '@/components/Badge';
import { Money } from '@/components/Money';
import { cn } from '@/lib/cn';
import { money } from '@/lib/money';
import type { Bill, BillAdjustment, BillPayment } from './api';
import { COUNTER_CURRENCY } from './config';
import {
  adjustmentLabel,
  formatClock,
  orderNumberText,
  paymentView,
  settlementView,
  tenderLabel,
} from './wording';
import { cardClasses } from '@/components/Card';
import { StatTile } from '@/components/StatTile';
import { SectionHeading } from '@/components/SectionHeading';

/** A server amount with the branch currency. No arithmetic. */
export const asMoney = (amount: number) => money(amount, COUNTER_CURRENCY);

import { Notice } from '@/components/Notice';

export { Notice };

/** True once `value` differs from what it was when the component first rendered (a server figure moved under the cashier). */
export function useChangedSince(value: number): boolean {
  const first = useRef(value);
  return first.current !== value;
}

/**
 * The three figures a cashier reads first, then the breakdown. All values are the backend's. A breakdown
 * row the backend reports as zero (no service charge, no discount) is left out; the figure is never changed.
 */
export function Amounts({ bill }: { bill: Bill }) {
  const settled = bill.remaining_balance === 0;
  return (
    <section aria-label="Bill amounts" className="flex flex-col gap-3">
      <dl className="grid gap-3 sm:grid-cols-3">
        <StatTile
          label="Balance due"
          value={<Money value={asMoney(bill.remaining_balance)} />}
          tone={settled ? 'success' : 'attention'}
          size="lg"
          testId="remaining"
          className="sm:order-last"
        />
        <StatTile label="Total" value={<Money value={asMoney(bill.total_amount)} />} />
        <StatTile label="Paid" value={<Money value={asMoney(bill.paid_amount)} />} />
      </dl>
      <dl className={cn(cardClasses({ padding: 'none' }), 'px-4 py-2 text-sm')}>
        <Row label="Subtotal" amount={bill.subtotal_amount} />
        {bill.tax_amount !== 0 && <Row label="Tax" amount={bill.tax_amount} />}
        {bill.service_charge_amount !== 0 && (
          <Row label="Service charge" amount={bill.service_charge_amount} />
        )}
        {bill.discount_amount !== 0 && <Row label="Discount" amount={bill.discount_amount} minus />}
      </dl>
    </section>
  );
}

function Row({ label, amount, minus }: { label: string; amount: number; minus?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium text-ink tabular-nums">
        {minus ? '−' : ''}
        <Money value={asMoney(amount)} />
      </dd>
    </div>
  );
}

export function SettlementBadge({ status }: { status: string }) {
  const view = settlementView(status);
  return <Badge tone={view.tone}>{view.label}</Badge>;
}

export function BillLines({ bill }: { bill: Bill }) {
  if (bill.items.length === 0) {
    return <p className="text-sm text-muted">No items on this bill.</p>;
  }
  return (
    <ul className={cn(cardClasses({ padding: 'none' }), 'divide-y divide-line')}>
      {bill.items.map((item) => (
        <li key={item.order_item_id} className="flex items-start justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="text-base font-semibold break-words text-ink">
              <span className="tabular-nums">{item.quantity}×</span> {item.name}
            </p>
            {item.modifiers.length > 0 && (
              <p className="text-sm break-words text-muted">{item.modifiers.join(', ')}</p>
            )}
            <p className="text-sm text-muted">
              {orderNumberText(item.order_number)} · <Money value={asMoney(item.unit_amount)} />{' '}
              each
            </p>
          </div>
          <p className="shrink-0 text-base font-bold text-ink tabular-nums">
            <Money value={asMoney(item.line_amount)} />
          </p>
        </li>
      ))}
    </ul>
  );
}

export function AdjustmentList({ adjustments }: { adjustments: BillAdjustment[] }) {
  if (adjustments.length === 0) return null;
  return (
    <section aria-labelledby="adjustments-title" className="flex flex-col gap-2">
      <SectionHeading id="adjustments-title" level={3}>
        Adjustments
      </SectionHeading>
      <ul className={cn(cardClasses({ padding: 'none' }), 'divide-y divide-line')}>
        {adjustments.map((a) => (
          <li key={a.adjustment_id} className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="text-base font-medium text-ink">{adjustmentLabel(a.adjustment_type)}</p>
              <p className="text-sm break-words text-muted">{a.reason_code ?? a.reason}</p>
              {a.reason_code && a.reason && a.reason !== a.reason_code && (
                <p className="text-sm break-words text-muted">{a.reason}</p>
              )}
              <p className="text-sm text-muted">{formatClock(a.created_at)}</p>
            </div>
            <p className="shrink-0 text-base font-medium text-ink tabular-nums">
              −<Money value={asMoney(Math.abs(a.amount))} />
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PaymentList({ payments }: { payments: BillPayment[] }) {
  if (payments.length === 0) return null;
  return (
    <section aria-labelledby="payments-title" className="flex flex-col gap-2">
      <SectionHeading id="payments-title" level={3}>
        Payments
      </SectionHeading>
      <ul className={cn(cardClasses({ padding: 'none' }), 'divide-y divide-line')}>
        {payments.map((p) => {
          const view = paymentView(p.status);
          const counted = p.status === 'SETTLED';
          // An unfinished online payment is named in one line, like the customer's bill ("Online · not
          // completed"); every other status keeps its badge.
          const notCompleted = p.status === 'PENDING';
          return (
            <li key={p.payment_id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-base font-medium text-ink">
                  {notCompleted
                    ? `${tenderLabel(p.tender_type)} · not completed`
                    : tenderLabel(p.tender_type)}
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-muted">
                  {!notCompleted && <Badge tone={view.tone}>{view.label}</Badge>}
                  {formatClock(p.created_at)}
                </p>
              </div>
              <p
                className={cn(
                  'shrink-0 text-base font-medium tabular-nums',
                  counted ? 'text-ink' : 'text-faint line-through',
                )}
              >
                <Money value={asMoney(p.amount)} />
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * One calm amber notice for everything the cashier should know before taking money: items still in the
 * kitchen, an online payment that was not completed. Nothing here is an error, so nothing is red.
 */
export function BillNotes({ bill, hasPendingOrders }: { bill: Bill; hasPendingOrders: boolean }) {
  const notes: string[] = [];
  if (hasPendingOrders)
    notes.push('Some items are still in the kitchen, so the bill can still grow.');
  if (pendingCount(bill) > 0) {
    notes.push(
      'An online payment was not completed. It does not reduce the balance due, and closing the session cancels it.',
    );
  }
  if (notes.length === 0) return null;
  return (
    <Notice tone="warn">
      {notes.length === 1 ? (
        notes[0]
      ) : (
        <ul className="flex list-disc flex-col gap-1 pl-4">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </Notice>
  );
}

/** Number of online payments the backend lists as pending. A count of server rows, not money. */
export function pendingCount(bill: Bill): number {
  return bill.payments.filter((p) => p.status === 'PENDING').length;
}
